import React, { useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Stack,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Chip,
  IconButton,
  Divider,
  Avatar,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import PersonIcon from "@mui/icons-material/Person"
import PhoneIcon from "@mui/icons-material/Phone"
import LabelIcon from "@mui/icons-material/Label"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"

export function CustomerSelect({ customers, onSelect }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const [searchTerm, setSearchTerm] = useState("")
  const [modalOpen, setModalOpen] = useState(false)

  const filteredCustomers =
    customers &&
    customers.filter((cust) => {
      const searchRegex = new RegExp(searchTerm, "i")
      return (
        searchRegex.test(cust.name) ||
        searchRegex.test(cust.description) ||
        searchRegex.test(cust.phone) ||
        searchRegex.test(cust.vat) ||
        searchRegex.test(cust.label)
      )
    })

  const handleSelect = (id) => {
    setModalOpen(false)
    setSearchTerm("")
    onSelect(id)
  }

  const handleClose = () => {
    setModalOpen(false)
    setSearchTerm("")
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setModalOpen(true)}
        sx={{
          textTransform: "none",
          fontSize: { xs: "0.8rem", sm: "0.875rem" },
        }}
      >
        {t("Select")}
      </Button>

      <Dialog
        open={modalOpen}
        onClose={handleClose}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            maxHeight: isMobile ? "100%" : "80vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #e2e8f0",
            bgcolor: "#f8fafc",
            py: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <PersonIcon sx={{ color: "#3b82f6", fontSize: { xs: 20, sm: 24 } }} />
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                fontWeight={600}
                color="#1e293b"
              >
                {t("Select a Customer")}
              </Typography>
            </Stack>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
          {/* Search Input - Fixed at top */}
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderBottom: "1px solid #e2e8f0",
              bgcolor: "#fff",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder={t("Search customers...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#64748b", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#f8fafc",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                },
              }}
            />
          </Box>

          {/* Customer List - Scrollable */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {filteredCustomers && filteredCustomers.length > 0 ? (
              <List disablePadding>
                {filteredCustomers.map((cust, index) => (
                  <React.Fragment key={cust.id || index}>
                    {index > 0 && <Divider />}
                    <ListItemButton
                      onClick={() => handleSelect(cust.id)}
                      sx={{
                        py: { xs: 1.5, sm: 2 },
                        px: { xs: 2, sm: 3 },
                        "&:hover": { bgcolor: "#f8fafc" },
                        "&:active": { bgcolor: "#e2e8f0" },
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        spacing={{ xs: 1.5, sm: 2 }}
                        sx={{ width: "100%" }}
                      >
                        {/* Avatar */}
                        <Avatar
                          sx={{
                            bgcolor: "#dbeafe",
                            color: "#3b82f6",
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            flexShrink: 0,
                          }}
                        >
                          <PersonIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        </Avatar>

                        {/* Customer Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Name */}
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="#1e293b"
                            sx={{
                              fontSize: { xs: "0.875rem", sm: "0.9rem" },
                              wordBreak: "break-word",
                              mb: 0.5,
                            }}
                          >
                            {cust.name}
                          </Typography>

                          {/* Label Chip */}
                          {cust.label && (
                            <Chip
                              icon={<LabelIcon sx={{ fontSize: 14 }} />}
                              label={cust.label}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: "0.7rem",
                                mb: 0.75,
                                bgcolor: "#f1f5f9",
                                color: "#475569",
                                "& .MuiChip-icon": { color: "#64748b" },
                              }}
                            />
                          )}

                          {/* Phone & VAT */}
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={{ xs: 0.25, sm: 2 }}
                            sx={{ mt: 0.5 }}
                          >
                            {cust.phone && (
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <PhoneIcon sx={{ fontSize: 14, color: "#64748b" }} />
                                <Typography
                                  variant="caption"
                                  color="#64748b"
                                  sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
                                >
                                  {cust.phone}
                                </Typography>
                              </Stack>
                            )}
                            {cust.vat && (
                              <Typography
                                variant="caption"
                                color="#64748b"
                                sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
                              >
                                VAT: {cust.vat}
                              </Typography>
                            )}
                          </Stack>
                        </Box>

                        {/* Select Icon */}
                        <CheckCircleIcon
                          sx={{
                            color: "#3b82f6",
                            fontSize: { xs: 20, sm: 24 },
                            flexShrink: 0,
                            opacity: 0.7,
                          }}
                        />
                      </Stack>
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="#64748b" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                  {searchTerm ? t("No customers found") : t("No customers available")}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Results count - Fixed at bottom on mobile */}
          {filteredCustomers && filteredCustomers.length > 0 && (
            <Box
              sx={{
                p: { xs: 1, sm: 1.5 },
                borderTop: "1px solid #e2e8f0",
                bgcolor: "#f8fafc",
                textAlign: "center",
              }}
            >
              <Typography variant="caption" color="#64748b">
                {filteredCustomers.length} {t("customers")}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

CustomerSelect.propTypes = {
  customers: PropTypes.array,
  onSelect: PropTypes.func,
}

export default CustomerSelect
